import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { PasswordInput } from '@/components/auth/PasswordInput';
import { Loader2, Stethoscope, AlertCircle, ArrowLeft, Shield, Clock, XCircle, CheckCircle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';

type ApplicationStatus = {
  status: 'pending_verification' | 'under_review' | 'rejected' | 'suspended';
  submittedAt?: string;
  rejectionReason?: string | null;
} | null;

export default function DoctorLogin() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [applicationStatus, setApplicationStatus] = useState<ApplicationStatus>(null);

  const { signIn, isAdmin } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setApplicationStatus(null);
    setIsLoading(true);

    try {
      const { error: signInError } = await signIn(email, password);
      if (signInError) {
        setError(signInError.message);
        return;
      }

      // Check verification status directly — don't wait for AuthContext async load
      const { data: { user: authUser } } = await supabase.auth.getUser();
      if (!authUser) { setError('Authentication failed'); return; }

      const { data: vr } = await (supabase
        .from('verification_requests' as any)
        .select('status, rejection_reason, submitted_at')
        .eq('doctor_id', authUser.id)
        .order('submitted_at', { ascending: false })
        .limit(1)
        .maybeSingle() as any);

      if (vr) {
        if (vr.status === 'pending_verification' || vr.status === 'under_review') {
          setApplicationStatus({ status: vr.status, submittedAt: vr.submitted_at });
          return;
        }
        if (vr.status === 'rejected') {
          setApplicationStatus({ status: 'rejected', rejectionReason: vr.rejection_reason });
          return;
        }
        if (vr.status === 'suspended') {
          setApplicationStatus({ status: 'suspended' });
          return;
        }
        // approved — let normal role-based routing handle it
      }

      // Route based on role
      if (isAdmin) navigate('/admin', { replace: true });
      else navigate('/doctor', { replace: true });
    } catch {
      setError('An unexpected error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 relative overflow-hidden">
      <div className="absolute inset-0 grid-pattern opacity-30" />
      <div className="absolute top-0 left-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-3xl animate-float-slow" />
      <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-primary/10 rounded-full blur-3xl animate-float-delayed" />

      <motion.div
        className="w-full max-w-md relative z-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6 group"
        >
          <ArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1" />
          Back to home
        </Link>

        {/* ── Application status screens ─────────────────────── */}
        {applicationStatus && (
          <Card className="border-border/30 bg-card/80 backdrop-blur-xl shadow-2xl overflow-hidden mb-4">
            <div className={`h-1 ${applicationStatus.status === 'rejected' || applicationStatus.status === 'suspended' ? 'bg-destructive' : 'bg-amber-500'}`} />
            <div className="p-6 space-y-4 text-center">
              {(applicationStatus.status === 'pending_verification' || applicationStatus.status === 'under_review') && (
                <>
                  <Clock className="h-12 w-12 text-amber-500 mx-auto" />
                  <h2 className="text-lg font-semibold">Application Under Review</h2>
                  <p className="text-sm text-muted-foreground">
                    Your application is <strong>{applicationStatus.status === 'under_review' ? 'currently being reviewed' : 'pending verification'}</strong>.
                    Our team will email you within 2–3 business days.
                  </p>
                  {applicationStatus.submittedAt && (
                    <p className="text-xs text-muted-foreground">
                      Submitted: {new Date(applicationStatus.submittedAt).toLocaleDateString()}
                    </p>
                  )}
                </>
              )}
              {applicationStatus.status === 'rejected' && (
                <>
                  <XCircle className="h-12 w-12 text-destructive mx-auto" />
                  <h2 className="text-lg font-semibold">Application Not Approved</h2>
                  {applicationStatus.rejectionReason && (
                    <p className="text-sm text-muted-foreground">
                      Reason: <span className="text-foreground">{applicationStatus.rejectionReason}</span>
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground">
                    Contact <a href="mailto:support@apnedoctors.com" className="text-primary hover:underline">support@apnedoctors.com</a> for more information.
                  </p>
                </>
              )}
              {applicationStatus.status === 'suspended' && (
                <>
                  <AlertCircle className="h-12 w-12 text-destructive mx-auto" />
                  <h2 className="text-lg font-semibold">Account Suspended</h2>
                  <p className="text-sm text-muted-foreground">
                    Your account has been suspended. Contact{' '}
                    <a href="mailto:support@apnedoctors.com" className="text-primary hover:underline">support@apnedoctors.com</a>.
                  </p>
                </>
              )}
              <Button variant="outline" className="w-full" onClick={() => { setApplicationStatus(null); setError(null); }}>
                Back to Login
              </Button>
            </div>
          </Card>
        )}

        {!applicationStatus && (
        <>
        <div className="flex items-center justify-center gap-2 mb-8">
          <motion.div
            className="relative"
            animate={{ scale: [1, 1.05, 1] }}
            transition={{ duration: 2, repeat: Infinity }}
          >
            <Stethoscope className="h-10 w-10 text-secondary" />
            <div className="absolute inset-0 bg-secondary/30 blur-xl rounded-full" />
          </motion.div>
          <span className="text-2xl font-bold text-foreground">
            Apne<span className="text-primary">Doctors</span>
          </span>
        </div>

        <Card className="border-border/30 bg-card/80 backdrop-blur-xl shadow-2xl overflow-hidden">
          <div className="h-1 bg-gradient-to-r from-secondary via-primary to-secondary" />

          <CardHeader className="space-y-4 pt-8 text-center">
            <div className="relative mx-auto w-fit">
              <div className="relative z-10 p-4 rounded-2xl bg-gradient-to-br from-card to-card/50 border border-border/50 shadow-lg">
                <Stethoscope className="h-10 w-10 text-secondary" />
              </div>
            </div>
            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-secondary/10 text-secondary border border-secondary/20 mx-auto">
              <Shield className="h-3 w-3" />
              Verified Medical Professional
            </div>
            <h1 className="text-2xl font-bold text-foreground">Doctor Portal</h1>
            <p className="text-sm text-muted-foreground max-w-xs mx-auto">
              Access your clinical workspace, patient records, and consultation queue.
            </p>
          </CardHeader>

          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4 px-6">
              {error && (
                <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}>
                  <Alert variant="destructive" className="border-destructive/30 bg-destructive/10">
                    <AlertCircle className="h-4 w-4" />
                    <AlertDescription>{error}</AlertDescription>
                  </Alert>
                </motion.div>
              )}

              <div className="space-y-2">
                <Label htmlFor="email" className="text-sm font-medium">
                  Professional Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="doctor@hospital.com"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  disabled={isLoading}
                  className="bg-background/50 border-border/50 focus:border-secondary focus:ring-secondary/20 transition-all"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium">
                  Password
                </Label>
                <PasswordInput
                  id="password"
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={isLoading}
                  className="bg-background/50 border-border/50 focus:border-secondary focus:ring-secondary/20"
                />
              </div>

              <div className="text-right">
                <Link
                  to="/forgot-password"
                  className="text-xs text-secondary hover:text-secondary/80 transition-colors"
                >
                  Forgot password?
                </Link>
              </div>
            </CardContent>

            <CardFooter className="flex flex-col gap-4 px-6 pb-8">
              <Button
                type="submit"
                className="w-full h-12 text-base font-semibold bg-secondary hover:bg-secondary/90"
                disabled={isLoading}
              >
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Access Doctor Console'
                )}
              </Button>

              <p className="text-sm text-muted-foreground text-center">
                New to ApneDoctors?{' '}
                <Link
                  to="/doctor/signup"
                  className="text-secondary hover:text-secondary/80 font-medium transition-colors"
                >
                  Apply for access
                </Link>
              </p>

              <p className="text-xs text-muted-foreground text-center">
                Patient?{' '}
                <Link to="/login" className="text-primary hover:underline">
                  Patient login
                </Link>
              </p>

              <p className="text-xs text-muted-foreground/60 text-center">
                By signing in you agree to our{' '}
                <Link to="/terms" className="hover:underline">Terms</Link>
                {' '}and{' '}
                <Link to="/privacy" className="hover:underline">Privacy Policy</Link>.
              </p>
            </CardFooter>
          </form>
        </Card>
        </>
        )}
      </motion.div>
    </div>
  );
}
